import { useEffect, useState, FC } from 'react';
import { CircularProgress, Typography, Box, Button } from '@mui/material';
import {
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import DocumentService, { downloadInBrowser } from 'services/document';
import { FileData, S3Object } from 'API';
import { getCacheWithExpiration } from 'shared/utilities';
import { DICTIONARY } from 'shared/constants';
import IconDownloadButton from 'components/IconDownloadButton';
import DocxViewer from './DocxViewer';

interface Props {
  className?: string;
  s3Object: S3Object;
  file: FileData
}

type signedURLType = {
  uri:string,
  fileType:string,
  fileName:string,
};

const FileViewer: FC<Props> = ({ className, s3Object, file }) => {
  const [signedURL, setSignedURL] = useState<signedURLType>({
    fileType: '',
    uri: '',
    fileName: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const { fileType, uri } = signedURL;
  // Only .docx renders in-browser; other Office formats fall through to download.
  const extension = file.fileName?.split('.').pop()?.toLowerCase();
  const isDocx = extension === 'docx'
    || file?.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  useEffect(() => {
    let ignore = false;
    const getURL = async () => {
      try {
        const url = await getCacheWithExpiration(
          JSON.stringify(s3Object),
          async () => DocumentService.getDocument(s3Object),
          14 * 60 * 1000, // 14 minutes
        );

        const { signedUrl, contentType, fileName } = url;

        if (!ignore) {
          setSignedURL({ fileType: contentType, uri: signedUrl, fileName });
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    getURL();

    return () => { ignore = true; };
  }, []);

  const handleDownload = () => {
    if (signedURL) {
      const { uri: signedUrl, fileName } = signedURL;
      downloadInBrowser(signedUrl, fileName);
    }
  };

  return (
    <Box
      component="figure"
      className={className}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
    >

      {isLoading && (
        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', width: '100%' }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !signedURL
          && <Typography>{DICTIONARY.DownloadError}</Typography>}

      {!isLoading && fileType && uri && (
        isDocx
          ? (
            <>
              <Box
                sx={(t) => ({
                  borderBottom: `1px solid ${t.palette.divider}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.25rem 0.5rem',
                  width: '100%',
                  '& > *': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
                })}
              >
                <Box sx={{ padding: '0.75rem' }}>
                  <Button
                    sx={{ marginLeft: '0.5rem' }}
                    color="secondary"
                    onClick={handleDownload}
                    disabled={!uri}
                    startIcon={<GetAppIcon />}
                  >
                    Download
                  </Button>
                </Box>
              </Box>

              <DocxViewer uri={uri} />
            </>
          )
          : (
            <Box
              className={className}
              sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', width: '100%' }}
            >
              <Box sx={{ maxWidth: '20rem', padding: '1rem', textAlign: 'center' }}>
                <Typography sx={{ marginBottom: '1rem' }}>
                  This file is not viewable within the platform, but it may be downloaded.
                </Typography>
                <Box sx={{ minHeight: '4rem' }}>
                  <IconDownloadButton file={file} />
                </Box>
              </Box>
            </Box>
          )
      )}
    </Box>
  );
};
export default FileViewer;
